import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Autocomplete,
} from '@mui/material';
import {
  LocalOffer as CouponsIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import * as adminApi from '../../api/admin';
import type { Coupon, CreateCouponInput, UpdateCouponInput, CouponType, PaymentProduct } from '../../api/admin';
import { useDocumentTitle } from '../../hooks/core';

export function CouponsPage() {
  useDocumentTitle('Coupons');

  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [products, setProducts] = useState<PaymentProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  // Create coupon dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [newCoupon, setNewCoupon] = useState<CreateCouponInput>({
    code: '',
    type: 'percent',
    currency: 'usd',
  });

  // Edit coupon dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editCoupon, setEditCoupon] = useState<Coupon | null>(null);
  const [editInput, setEditInput] = useState<UpdateCouponInput>({});

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [couponsData, productsData] = await Promise.all([
        adminApi.listCoupons(showInactive),
        adminApi.listProducts(),
      ]);
      setCoupons(couponsData);
      setProducts(productsData.filter(p => p.active));
      setError(null);
    } catch (err) {
      setError('Failed to load coupons');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateCoupon = async () => {
    try {
      setCreateLoading(true);
      setCreateError(null);
      await adminApi.createCoupon(newCoupon);
      setSuccess('Coupon created successfully');
      setCreateDialogOpen(false);
      resetCreateForm();
      fetchData();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create coupon';
      setCreateError(errorMessage);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleUpdateCoupon = async () => {
    if (!editCoupon) return;
    try {
      setEditLoading(true);
      setEditError(null);
      await adminApi.updateCoupon(editCoupon.id, editInput);
      setSuccess('Coupon updated successfully');
      setEditDialogOpen(false);
      fetchData();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update coupon';
      setEditError(errorMessage);
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteCoupon = async (coupon: Coupon) => {
    if (!confirm(`Are you sure you want to delete the coupon "${coupon.code}"?`)) return;
    try {
      await adminApi.deleteCoupon(coupon.id);
      setSuccess('Coupon deleted successfully');
      fetchData();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete coupon';
      setError(errorMessage);
    }
  };

  const handleCopyCode = async (code: string) => {
    // Try modern clipboard API first
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(code);
        setSuccess(`Copied "${code}" to clipboard`);
        return;
      } catch (err) {
        console.warn('Clipboard API failed, trying fallback:', err);
      }
    }

    // Fallback for non-secure contexts or when clipboard API fails
    try {
      const textArea = document.createElement('textarea');
      textArea.value = code;
      textArea.style.position = 'fixed';
      textArea.style.top = '0';
      textArea.style.left = '0';
      textArea.style.width = '2em';
      textArea.style.height = '2em';
      textArea.style.padding = '0';
      textArea.style.border = 'none';
      textArea.style.outline = 'none';
      textArea.style.boxShadow = 'none';
      textArea.style.background = 'transparent';
      textArea.style.opacity = '0';
      textArea.style.zIndex = '99999';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      textArea.setSelectionRange(0, code.length);
      const success = document.execCommand('copy');
      document.body.removeChild(textArea);
      if (success) {
        setSuccess(`Copied "${code}" to clipboard`);
      } else {
        setError('Failed to copy to clipboard');
      }
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      setError('Failed to copy to clipboard');
    }
  };

  const resetCreateForm = () => {
    setNewCoupon({
      code: '',
      type: 'percent',
      currency: 'usd',
    });
    setCreateError(null);
  };

  const openEditDialog = (coupon: Coupon) => {
    setEditCoupon(coupon);
    setEditInput({
      validFrom: coupon.validFrom,
      validUntil: coupon.validUntil,
      maxRedemptions: coupon.maxRedemptions,
      maxPerUser: coupon.maxPerUser,
      applicableProducts: coupon.applicableProducts,
      active: coupon.active,
    });
    setEditError(null);
    setEditDialogOpen(true);
  };

  const formatDiscount = (coupon: Coupon): string => {
    if (coupon.type === 'percent' && coupon.percentOff) {
      return `${coupon.percentOff}% off`;
    } else if (coupon.type === 'fixed' && coupon.amountOff) {
      return `$${(coupon.amountOff / 100).toFixed(2)} off`;
    }
    return '-';
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const isExpired = (coupon: Coupon): boolean => {
    if (!coupon.validUntil) return false;
    return new Date(coupon.validUntil) < new Date();
  };

  const isMaxedOut = (coupon: Coupon): boolean => {
    if (!coupon.maxRedemptions) return false;
    return coupon.redemptionCount >= coupon.maxRedemptions;
  };

  const getStatusChip = (coupon: Coupon) => {
    if (!coupon.active) {
      return <Chip label="Inactive" size="small" color="default" />;
    }
    if (isExpired(coupon)) {
      return <Chip label="Expired" size="small" color="warning" />;
    }
    if (isMaxedOut(coupon)) {
      return <Chip label="Max Used" size="small" color="warning" />;
    }
    return <Chip label="Active" size="small" color="success" />;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CouponsIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h4">Coupons</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
            }
            label="Show inactive"
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Coupon
          </Button>
        </Box>
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Create and manage discount coupons for your products. Coupons can offer percentage or fixed amount discounts.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Discount</TableCell>
                <TableCell>Valid Period</TableCell>
                <TableCell>Redemptions</TableCell>
                <TableCell>Products</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {coupons.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No coupons found. Create a coupon to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                coupons.map((coupon) => (
                  <TableRow key={coupon.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography fontWeight="medium" fontFamily="monospace">
                          {coupon.code}
                        </Typography>
                        <Tooltip title="Copy code">
                          <IconButton size="small" onClick={() => handleCopyCode(coupon.code)}>
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={formatDiscount(coupon)}
                        size="small"
                        color={coupon.type === 'percent' ? 'primary' : 'secondary'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {coupon.validFrom || coupon.validUntil ? (
                          <>
                            {formatDate(coupon.validFrom)} - {formatDate(coupon.validUntil)}
                          </>
                        ) : (
                          'No limit'
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {coupon.redemptionCount}
                        {coupon.maxRedemptions ? ` / ${coupon.maxRedemptions}` : ''}
                      </Typography>
                      {coupon.maxPerUser && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Max {coupon.maxPerUser} per user
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {coupon.applicableProducts && coupon.applicableProducts.length > 0 ? (
                        <Tooltip title={coupon.applicableProducts.map(id => {
                          const p = products.find(prod => prod.id === id);
                          return p?.name || id;
                        }).join(', ')}>
                          <Chip
                            label={`${coupon.applicableProducts.length} product${coupon.applicableProducts.length > 1 ? 's' : ''}`}
                            size="small"
                            variant="outlined"
                          />
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          All products
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {getStatusChip(coupon)}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEditDialog(coupon)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleDeleteCoupon(coupon)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create Coupon Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Coupon</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {createError && (
              <Alert severity="error" onClose={() => setCreateError(null)}>
                {createError}
              </Alert>
            )}
            <TextField
              label="Coupon Code"
              value={newCoupon.code}
              onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })}
              helperText="Code will be converted to uppercase"
              fullWidth
              required
            />
            <FormControl fullWidth>
              <InputLabel>Discount Type</InputLabel>
              <Select
                value={newCoupon.type}
                label="Discount Type"
                onChange={(e) => setNewCoupon({
                  ...newCoupon,
                  type: e.target.value as CouponType,
                  amountOff: undefined,
                  percentOff: undefined,
                })}
              >
                <MenuItem value="percent">Percentage Off</MenuItem>
                <MenuItem value="fixed">Fixed Amount Off</MenuItem>
              </Select>
            </FormControl>

            {newCoupon.type === 'percent' ? (
              <TextField
                label="Percent Off"
                type="number"
                value={newCoupon.percentOff || ''}
                onChange={(e) => setNewCoupon({ ...newCoupon, percentOff: parseFloat(e.target.value) || undefined })}
                helperText="Enter a percentage (e.g., 10 for 10% off)"
                fullWidth
                required
                inputProps={{ min: 0, max: 100, step: 0.1 }}
              />
            ) : (
              <TextField
                label="Amount Off (cents)"
                type="number"
                value={newCoupon.amountOff || ''}
                onChange={(e) => setNewCoupon({ ...newCoupon, amountOff: parseInt(e.target.value) || undefined })}
                helperText={newCoupon.amountOff ? `Display: $${(newCoupon.amountOff / 100).toFixed(2)}` : 'Enter amount in cents'}
                fullWidth
                required
                inputProps={{ min: 0 }}
              />
            )}

            <TextField
              label="Valid From"
              type="datetime-local"
              value={newCoupon.validFrom ? newCoupon.validFrom.slice(0, 16) : ''}
              onChange={(e) => setNewCoupon({ ...newCoupon, validFrom: e.target.value ? new Date(e.target.value).toISOString() : undefined })}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Optional: Leave empty for no start date"
            />

            <TextField
              label="Valid Until"
              type="datetime-local"
              value={newCoupon.validUntil ? newCoupon.validUntil.slice(0, 16) : ''}
              onChange={(e) => setNewCoupon({ ...newCoupon, validUntil: e.target.value ? new Date(e.target.value).toISOString() : undefined })}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Optional: Leave empty for no expiration"
            />

            <TextField
              label="Max Total Redemptions"
              type="number"
              value={newCoupon.maxRedemptions || ''}
              onChange={(e) => setNewCoupon({ ...newCoupon, maxRedemptions: parseInt(e.target.value) || undefined })}
              helperText="Optional: Leave empty for unlimited"
              fullWidth
              inputProps={{ min: 1 }}
            />

            <TextField
              label="Max Uses Per User"
              type="number"
              value={newCoupon.maxPerUser || ''}
              onChange={(e) => setNewCoupon({ ...newCoupon, maxPerUser: parseInt(e.target.value) || undefined })}
              helperText="Optional: Leave empty for unlimited per user"
              fullWidth
              inputProps={{ min: 1 }}
            />

            <Autocomplete
              multiple
              options={products}
              getOptionLabel={(option) => option.name}
              value={products.filter(p => newCoupon.applicableProducts?.includes(p.id))}
              onChange={(_, values) => setNewCoupon({
                ...newCoupon,
                applicableProducts: values.length > 0 ? values.map(v => v.id) : undefined,
              })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Applicable Products"
                  helperText="Optional: Leave empty to apply to all products"
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateCoupon}
            variant="contained"
            disabled={createLoading || !newCoupon.code || (newCoupon.type === 'percent' ? !newCoupon.percentOff : !newCoupon.amountOff)}
          >
            {createLoading ? <CircularProgress size={24} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Coupon Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Coupon: {editCoupon?.code}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {editError && (
              <Alert severity="error" onClose={() => setEditError(null)}>
                {editError}
              </Alert>
            )}

            <Alert severity="info">
              Coupon code, type, and discount amount cannot be changed after creation.
            </Alert>

            <Typography variant="body2" color="text.secondary">
              Current discount: {editCoupon ? formatDiscount(editCoupon) : '-'}
            </Typography>

            <TextField
              label="Valid From"
              type="datetime-local"
              value={editInput.validFrom ? editInput.validFrom.slice(0, 16) : ''}
              onChange={(e) => setEditInput({ ...editInput, validFrom: e.target.value ? new Date(e.target.value).toISOString() : undefined })}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Valid Until"
              type="datetime-local"
              value={editInput.validUntil ? editInput.validUntil.slice(0, 16) : ''}
              onChange={(e) => setEditInput({ ...editInput, validUntil: e.target.value ? new Date(e.target.value).toISOString() : undefined })}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Max Total Redemptions"
              type="number"
              value={editInput.maxRedemptions || ''}
              onChange={(e) => setEditInput({ ...editInput, maxRedemptions: parseInt(e.target.value) || undefined })}
              helperText={`Current: ${editCoupon?.redemptionCount || 0} redemptions`}
              fullWidth
              inputProps={{ min: 1 }}
            />

            <TextField
              label="Max Uses Per User"
              type="number"
              value={editInput.maxPerUser || ''}
              onChange={(e) => setEditInput({ ...editInput, maxPerUser: parseInt(e.target.value) || undefined })}
              fullWidth
              inputProps={{ min: 1 }}
            />

            <Autocomplete
              multiple
              options={products}
              getOptionLabel={(option) => option.name}
              value={products.filter(p => editInput.applicableProducts?.includes(p.id))}
              onChange={(_, values) => setEditInput({
                ...editInput,
                applicableProducts: values.length > 0 ? values.map(v => v.id) : undefined,
              })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Applicable Products"
                  helperText="Leave empty to apply to all products"
                />
              )}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={editInput.active ?? editCoupon?.active ?? true}
                  onChange={(e) => setEditInput({ ...editInput, active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpdateCoupon}
            variant="contained"
            disabled={editLoading}
          >
            {editLoading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
